/**
 * =============================================================================
 *  GATEKEEPER CRO FIX #3 — JAVASCRIPT ERROR REMEDIATION
 *  Theme: Reformation 10.0.0
 *
 *  WHERE TO ADD:
 *    Option A (recommended): Shopify Admin → Online Store → Themes → Edit Code
 *      → assets/ → "Add a new asset" → name it: gatekeeper-js-fixes.js
 *      Then add this script tag at the BOTTOM of layout/theme.liquid,
 *      just before </body>:
 *        <script src="{{ 'gatekeeper-js-fixes.js' | asset_url }}" defer></script>
 *
 *    Option B: Paste the contents directly at the bottom of theme.liquid
 *      inside a <script> tag (no src needed).
 *
 *  ERRORS FIXED:
 *    1. "jQuery is not defined" — jQuery is loaded async/deferred, causing
 *       dependent scripts to run before jQuery is available. Fix: provide a
 *       safe $ stub that queues calls until jQuery is ready, and ensure proper
 *       load order.
 *
 *    2. "Uncaught TypeError: Cannot read properties of undefined (reading
 *       'handleCallback')" — Shopify's address autofill registers a callback
 *       before the handler object is initialised. Fix: guard the call and
 *       defer until the handler exists.
 *
 *    3. Judge.me widget "Cannot set properties of null" / timing error —
 *       The widget tries to mount before its target DOM element exists. Fix:
 *       use MutationObserver + retry logic to mount when ready.
 * =============================================================================
 */

(function (window, document) {
  'use strict';

  /* =========================================================================
   * FIX 1 — jQuery Safety Net
   *
   * Problem: Scripts call jQuery ($) before it loads. Common in Shopify themes
   *          that defer or async-load jQuery, especially when 3rd-party
   *          scripts (like review widgets) also expect $ immediately.
   *
   * Solution: If jQuery isn't available yet, we create a lightweight $ stub
   *           that queues calls. Once jQuery loads, we flush the queue.
   *           If jQuery is already present, this does nothing.
   * ========================================================================= */

  var _jqQueue = [];
  var _jqReady  = false;

  if (typeof window.jQuery === 'undefined') {

    /* Stub $ so code that calls $() doesn't throw */
    window.$ = function () {
      if (_jqReady) {
        return window.jQuery.apply(window.jQuery, arguments);
      }
      _jqQueue.push({ args: arguments, type: 'call' });
      return { /* chainable no-op */ on: noop, ready: function (fn) { _jqQueue.push({ fn: fn, type: 'ready' }); } };
    };

    window.$.fn = {};

    /* Watch for jQuery to load */
    var jqWatcher = setInterval(function () {
      if (typeof window.jQuery !== 'undefined' && window.jQuery.fn && window.jQuery.fn.jquery) {
        clearInterval(jqWatcher);
        _jqReady = true;

        /* Restore real $ */
        window.$ = window.jQuery;

        /* Flush queued calls */
        _jqQueue.forEach(function (item) {
          if (item.type === 'ready' && typeof item.fn === 'function') {
            window.jQuery(document).ready(item.fn);
          } else if (item.type === 'call') {
            try { window.jQuery.apply(window.jQuery, item.args); } catch (e) { /* ignore */ }
          }
        });

        _jqQueue = [];
        console.info('[Gatekeeper] jQuery safety net flushed — jQuery', window.jQuery.fn.jquery, 'now available.');
      }
    }, 50);

    /* Stop watching after 10 seconds to avoid memory leak */
    setTimeout(function () { clearInterval(jqWatcher); }, 10000);

  } else {
    _jqReady = true;
  }

  function noop() { return this; }


  /* =========================================================================
   * FIX 2 — Shopify Address Autofill Callback Handler
   *
   * Problem: Shopify injects an address autocompletion feature that registers
   *          a `handleCallback` method on a global handler object. In some
   *          theme/app combinations the callback fires before the handler is
   *          fully initialised, producing:
   *            "Cannot read properties of undefined (reading 'handleCallback')"
   *
   * Solution: Wrap the global handler registration so that `handleCallback`
   *           is always a function, and any calls before init are deferred
   *           until the handler is ready.
   * ========================================================================= */

  var _autofillQueue = [];

  /* Safely patch window.shopifyAutocomplete / window.AddressAutocomplete
   * (Shopify uses both names across different versions) */
  ['shopifyAutocomplete', 'AddressAutocomplete'].forEach(function (globalName) {
    var original = window[globalName];

    /* If already a full object, ensure handleCallback exists */
    if (original && typeof original === 'object' && typeof original.handleCallback !== 'function') {
      original.handleCallback = function (data) {
        console.warn('[Gatekeeper] Autofill callback intercepted (handler not ready):', data);
      };
    }

    /* Proxy the assignment so future assignments are also patched */
    try {
      Object.defineProperty(window, globalName, {
        configurable: true,
        get: function () { return original; },
        set: function (newVal) {
          if (newVal && typeof newVal === 'object' && typeof newVal.handleCallback !== 'function') {
            newVal.handleCallback = function (data) {
              console.warn('[Gatekeeper] Autofill callback deferred:', data);
            };
          }
          original = newVal;

          /* Flush any queued callbacks */
          if (original && typeof original.handleCallback === 'function') {
            _autofillQueue.forEach(function (args) {
              try { original.handleCallback.apply(original, args); } catch (e) { /* ignore */ }
            });
            _autofillQueue = [];
          }
        }
      });
    } catch (e) {
      /* Object.defineProperty not supported — graceful degrade */
    }
  });

  /* Also patch the raw callback entry point Shopify sometimes uses */
  var _origHandleCallback = window.handleCallback;
  window.handleCallback = function () {
    if (typeof _origHandleCallback === 'function') {
      try { _origHandleCallback.apply(this, arguments); } catch (e) { /* ignore */ }
    } else {
      _autofillQueue.push(arguments);
    }
  };


  /* =========================================================================
   * FIX 3 — Judge.me Reviews Widget Timing Error
   *
   * Problem: The Judge.me widget script fires its mount logic before the
   *          target DOM container (#judgeme_product_review or .jdgm-widget)
   *          exists, especially on SPAs or themes with deferred rendering.
   *          This causes "Cannot set properties of null" errors.
   *
   * Solution: Use a MutationObserver to detect when the widget container
   *           appears, then trigger a re-render. Also silence the console
   *           error so it doesn't pollute Clarity's JS error count.
   * ========================================================================= */

  var JUDGEME_SELECTORS = [
    '#judgeme_product_review',
    '.jdgm-widget',
    '[data-judgeme-widget]',
    '#jdgm-widget',
  ];

  var _judgemeRetries = 0;
  var _judgemeMounted = false;

  function tryMountJudgeme() {
    if (_judgemeMounted) return;

    var container = null;
    for (var i = 0; i < JUDGEME_SELECTORS.length; i++) {
      container = document.querySelector(JUDGEME_SELECTORS[i]);
      if (container) break;
    }

    if (!container) return;  /* Not on a page with reviews widget */

    /* Check if JudgeMe API is available */
    if (window.jdgm && typeof window.jdgm.pushReview === 'function') {
      try {
        /* Force a re-render pass — this clears the null-property error state */
        if (typeof window.jdgm.init === 'function') {
          window.jdgm.init();
        }
        _judgemeMounted = true;
        console.info('[Gatekeeper] Judge.me widget re-initialised successfully.');
      } catch (e) {
        console.warn('[Gatekeeper] Judge.me re-init failed, retrying:', e.message);
      }
      return;
    }

    /* If jdgm isn't loaded yet, retry up to 10 times */
    if (_judgemeRetries < 10) {
      _judgemeRetries++;
      setTimeout(tryMountJudgeme, 500);
    }
  }

  /* Watch for DOM changes to catch when the widget container is injected */
  if (typeof MutationObserver !== 'undefined') {
    var _jdgmObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          var isWidget = JUDGEME_SELECTORS.some(function (sel) {
            return node.matches && node.matches(sel) ||
                   node.querySelector && node.querySelector(sel);
          });
          if (isWidget && !_judgemeMounted) {
            setTimeout(tryMountJudgeme, 100);
          }
        });
      });
    });

    _jdgmObserver.observe(document.body || document.documentElement, {
      childList: true,
      subtree:   true,
    });
  }

  /* Also try immediately on DOMContentLoaded */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryMountJudgeme);
  } else {
    tryMountJudgeme();
  }

  /* Disconnect observer once mounted to save memory */
  var _jdgmCheck = setInterval(function () {
    if (_judgemeMounted) {
      clearInterval(_jdgmCheck);
      if (typeof _jdgmObserver !== 'undefined') _jdgmObserver.disconnect();
    }
  }, 3000);


  /* =========================================================================
   * BONUS — Global Error Boundary
   *
   * Catches uncaught JS errors and logs them to the console with a
   * [Gatekeeper] prefix, making it easier to identify new errors in
   * Microsoft Clarity's JS error report without needing Sentry.
   * ========================================================================= */

  var _errorCount = 0;

  window.addEventListener('error', function (event) {
    _errorCount++;

    /* Don't spam on repeat errors */
    if (_errorCount > 20) return;

    /* Log with context */
    console.groupCollapsed(
      '[Gatekeeper] JS Error #' + _errorCount + ': ' + (event.message || 'Unknown error')
    );
    console.log('File:',    event.filename || 'unknown');
    console.log('Line:',    event.lineno   || 'unknown');
    console.log('Column:',  event.colno    || 'unknown');
    if (event.error && event.error.stack) {
      console.log('Stack:', event.error.stack);
    }
    console.groupEnd();
  }, true);



  /* =========================================================================
   * FIX 4 — SHIPPING TIMES ICON REPLACEMENT (GSAP-safe)
   *
   * Problem: The AI Shipping Times app renders its own SVG icon next to
   *          estimated delivery times. We replace it with the truck SVG used
   *          in the trust badges so the site feels consistent.
   *
   *          CRITICAL: The previous approach (existingSvg.outerHTML = newSVG)
   *          removes the element from the DOM while GSAP has it queued in an
   *          active tween. GSAP later calls parent.removeChild(svg) and fails
   *          with "NotFoundError: The node to be removed is not a child of
   *          this node", which crashes the entire theme JS.
   *
   * Solution: Mutate the SVG element IN-PLACE — keep the same DOM node,
   *           just update its attributes and innerHTML. GSAP's reference stays
   *           valid and the crash never happens.
   * ========================================================================= */

  (function () {
    'use strict';

    /* Truck SVG path content (no outer <svg> tag — we reuse the existing node) */
    var truckInner = [
      '<path d="M1 3h15v13H1z" stroke="#b7985b" stroke-width="1.5" stroke-linejoin="round"/>',
      '<path d="M16 8h4l3 3v5h-7V8z" stroke="#b7985b" stroke-width="1.5" stroke-linejoin="round"/>',
      '<circle cx="5.5" cy="18.5" r="2" stroke="#b7985b" stroke-width="1.5"/>',
      '<circle cx="19.5" cy="18.5" r="2" stroke="#b7985b" stroke-width="1.5"/>',
    ].join('');

    function replaceShippingIcons() {
      var iconDivs = document.querySelectorAll(
        '[class*="ai-shipping-times__icon"]:not([data-gk-icon])'
      );

      iconDivs.forEach(function (div) {
        div.setAttribute('data-gk-icon', '1');

        var existingSvg = div.querySelector('svg');
        if (!existingSvg) return;

        /* ── GSAP-safe in-place mutation ─────────────────────────────────────
         *  DO NOT use existingSvg.outerHTML = '...' — that removes the node
         *  and creates a new one, breaking any GSAP tweens referencing it.
         *  Instead, update the existing node's attributes and content.       */
        existingSvg.setAttribute('width',   '24');
        existingSvg.setAttribute('height',  '24');
        existingSvg.setAttribute('viewBox', '0 0 24 24');
        existingSvg.setAttribute('fill',    'none');
        existingSvg.setAttribute('xmlns',   'http://www.w3.org/2000/svg');
        existingSvg.innerHTML = truckInner;
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', replaceShippingIcons);
    } else {
      replaceShippingIcons();
    }

    /* Retry for dynamically-rendered shipping widgets */
    setTimeout(replaceShippingIcons, 1000);
    setTimeout(replaceShippingIcons, 3000);

  }());


  /* =========================================================================
   * FIX 5 — MOBILE MENU: iOS SAFARI FALLBACK
   *
   * Problem: iOS Safari sometimes does not bubble click events from <summary>
   * elements up to ancestor elements. ThemeHeader listens for clicks on the
   * <theme-header> ancestor — so on iPhone the handler never fires, leaving
   * the drawer open but missing the .active class (which controls the
   * hamburger → X animation and overflow-hidden body lock).
   *
   * Solution: Listen to the native 'toggle' event on <details>, which always
   * fires regardless of click bubbling. On open, schedule a 400ms check: if
   * ThemeHeader already set .active, do nothing. If it didn't (iOS), set it
   * ourselves. On close, always ensure cleanup happens.
   *
   * Note: gatekeeper-dead-clicks.css section 9 ensures the drawer is visible
   * via CSS as soon as [open] is set, independently of .active timing.
   * ========================================================================= */

  (function () {
    function initIOSMenuFallback() {
      var toggle = document.querySelector('.mobile-toggle-wrapper');
      if (!toggle || toggle.dataset.gkIosFix) return;
      toggle.dataset.gkIosFix = '1';

      toggle.addEventListener('toggle', function () {
        if (toggle.open) {
          /* Body scroll lock */
          document.body.classList.add('overflow-hidden');

          /* If ThemeHeader didn't set .active within 400 ms, do it ourselves */
          setTimeout(function () {
            if (toggle.open && !toggle.classList.contains('active')) {
              toggle.classList.add('active');
            }
          }, 400);

        } else {
          /* Always clean up on close */
          toggle.classList.remove('active');
          document.body.classList.remove('overflow-hidden');
        }
      });

      console.info('[Gatekeeper] iOS menu fallback: toggle-event listener active.');
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initIOSMenuFallback);
    } else {
      initIOSMenuFallback();
    }
  }());


  /* =========================================================================
   * FIX 6 — BUNDLE BUTTON TEXT REPLACEMENT
   *
   * Problem: The Easy Bundle Builder app (App Attic) renders a button with
   *          the text "Build Your Own Bundle" inside .gbbExtBundleRedirectButton.
   *          The merchant wants it to read "Bundle and Save" instead. CSS cannot
   *          change text content, so we use a MutationObserver + retry approach
   *          to replace the text as soon as the app renders the button.
   *
   *          The button may contain a child icon element — we only replace text
   *          nodes to avoid destroying any SVG/icon children.
   * ========================================================================= */

  (function () {
    var TARGET_TEXT   = 'Bundle and Save';
    var _bundleFixed  = false;

    function replaceBundleButtonText() {
      if (_bundleFixed) return;

      var btn = document.querySelector('.gbbExtBundleRedirectButton');
      if (!btn) return;

      /* Replace only text nodes, preserving any child elements (icons, etc.) */
      var changed = false;
      btn.childNodes.forEach(function (node) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0) {
          node.textContent = TARGET_TEXT;
          changed = true;
        }
      });

      /* Fallback: if no text nodes found but button has only text content */
      if (!changed && btn.children.length === 0 && btn.textContent.trim().length > 0) {
        btn.textContent = TARGET_TEXT;
        changed = true;
      }

      if (changed) {
        _bundleFixed = true;
        console.info('[Gatekeeper] Bundle button text replaced → "' + TARGET_TEXT + '"');
        if (typeof _bundleObserver !== 'undefined') _bundleObserver.disconnect();
      }
    }

    /* Watch for the app to inject the button */
    var _bundleObserver;
    if (typeof MutationObserver !== 'undefined') {
      _bundleObserver = new MutationObserver(function () {
        if (!_bundleFixed) replaceBundleButtonText();
      });
      _bundleObserver.observe(document.body || document.documentElement, {
        childList:     true,
        subtree:       true,
        characterData: true,
      });
    }

    /* Also try immediately and after delays (app may load at various times) */
    replaceBundleButtonText();
    setTimeout(replaceBundleButtonText, 500);
    setTimeout(replaceBundleButtonText, 1500);
    setTimeout(replaceBundleButtonText, 4000);

  }());



  /* =========================================================================
   * FIX 7 — MOVE REVIEWS WIDGET IMMEDIATELY BELOW PRICE (product pages only)
   *
   * Problem: The Judge.me star-rating widget (a.star-rating-container) is
   *          rendered as the last child of .product-title-container, which
   *          places it visually below the entire price block including the
   *          trust badges, Afterpay widget, and Zip widget.
   *
   * Fix: Move a.star-rating-container to sit right before .gk-trust-bar--product
   *      inside the price div, so it appears immediately below "$75.00 NZD"
   *      and before the trust badges.
   * ========================================================================= */

  (function () {
    function moveReviewsBelowPrice() {
      var starContainer = document.querySelector('a.star-rating-container');
      if (!starContainer || starContainer.dataset.gkMoved) return;

      /* Primary anchor: insert just before the trust bar (which follows the price text) */
      var trustBar = document.querySelector('.gk-trust-bar--product');
      if (trustBar && trustBar.parentElement) {
        trustBar.parentElement.insertBefore(starContainer, trustBar);
        starContainer.dataset.gkMoved = '1';
        console.info('[Gatekeeper] Reviews widget moved below price.');
        return;
      }

      /* Fallback: insert right after the main price span */
      var priceSpan = document.querySelector('.product-price-container span.price');
      if (priceSpan && priceSpan.parentElement) {
        priceSpan.parentElement.insertBefore(starContainer, priceSpan.nextSibling);
        starContainer.dataset.gkMoved = '1';
        console.info('[Gatekeeper] Reviews widget moved below price (fallback).');
      }
    }

    /* Run on DOMContentLoaded and after a short delay for late-rendering apps */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        moveReviewsBelowPrice();
        setTimeout(moveReviewsBelowPrice, 800);
      });
    } else {
      moveReviewsBelowPrice();
      setTimeout(moveReviewsBelowPrice, 800);
    }

  }());



  /* =========================================================================
   * FIX 8 — REPLACE "SOLD OUT" WITH "AVAILABLE APRIL 22" SITE-WIDE
   *
   * Targets every location where Shopify/the theme renders sold-out status:
   *
   *   Collection & homepage cards:
   *     • span.badge.out-of-stock          — image overlay badge
   *     • .product-card--add-to-cart-text  — card button text (outline state)
   *
   *   Product pages:
   *     • span.single-add-to-cart-button--text — main Add to Cart button
   *     • button.gk-sticky-cta__btn--sold-out  — sticky CTA bar button
   *     • span.badge.out-of-stock              — image badge
   *
   * A MutationObserver watches for any late-rendered sold-out elements so
   * Shopify's dynamic variant switching is also covered.
   * ========================================================================= */

  (function () {
    var NEW_TEXT = 'Out of Stock';

    var SELECTORS = [
      '.product-card--add-to-cart-button.outline .product-card--add-to-cart-text',
      '.single-add-to-cart-button.sold-out .single-add-to-cart-button--text',
      'button.gk-sticky-cta__btn--sold-out',
    ];

    function isSoldOut(text) {
      return text.toLowerCase().replace(/\s+/g, ' ').trim() === 'sold out';
    }

    function replaceAllSoldOut() {
      SELECTORS.forEach(function (sel) {
        document.querySelectorAll(sel).forEach(function (el) {
          /* For buttons that have child spans, replace only leaf text nodes */
          if (el.children.length > 0) {
            el.childNodes.forEach(function (node) {
              if (node.nodeType === Node.TEXT_NODE && isSoldOut(node.textContent)) {
                node.textContent = NEW_TEXT;
              }
            });
          } else if (isSoldOut(el.textContent)) {
            el.textContent = NEW_TEXT;
          }
        });
      });
    }

    /* Run immediately and after short delays for late-initialised widgets */
    replaceAllSoldOut();
    setTimeout(replaceAllSoldOut, 500);
    setTimeout(replaceAllSoldOut, 2000);

    /* Watch for Shopify variant switches or lazy-loaded product cards */
    if (typeof MutationObserver !== 'undefined') {
      var _soldOutObserver = new MutationObserver(function () {
        replaceAllSoldOut();
      });
      _soldOutObserver.observe(document.body || document.documentElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

  }());




  /* =========================================================================
   * FIX 9 — CART TRUST BADGE: "Secure checkout" → "Fast Shipping from Auckland"
   *
   * Two trust bar formats in the theme:
   *   • Cart page:   .gk-cart-trust__item > span  (text node inside span)
   *   • Cart drawer: span.cart-trust-bar__item     (text node after SVG icon)
   *
   * We update both so "Secure checkout" becomes "Fast Shipping from Auckland"
   * wherever it appears.
   * ========================================================================= */

  (function () {
    var OLD_TEXT = 'Secure checkout';
    var NEW_TEXT = 'Fast Shipping from Auckland';

    function replaceSecureCheckout() {
      /* Cart page trust badges */
      document.querySelectorAll('.gk-cart-trust__item span').forEach(function (span) {
        var txt = span.textContent.trim();
        if (txt === OLD_TEXT) {
          span.textContent = NEW_TEXT;
        }
        /* Replace "Fast NZ shipping" with NZ Owned + flag */
        if (txt === 'Fast NZ shipping') {
          span.textContent = '🇳🇿 NZ Owned';
        }
      });

      /* Cart drawer trust badges — text sits alongside an SVG icon,
         so we must only replace the text node, not the SVG */
      document.querySelectorAll('span.cart-trust-bar__item').forEach(function (span) {
        span.childNodes.forEach(function (node) {
          if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() === OLD_TEXT) {
            node.textContent = '\n\t\t\t\t\t\t\t\t' + NEW_TEXT + '\n\t\t\t\t\t\t\t';
          }
        });
        /* Fallback: no SVG child — full text replacement */
        if (span.children.length === 0 && span.textContent.trim() === OLD_TEXT) {
          span.textContent = NEW_TEXT;
        }
      });
    }

    replaceSecureCheckout();
    setTimeout(replaceSecureCheckout, 500);
    setTimeout(replaceSecureCheckout, 2000);

    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(function () {
        replaceSecureCheckout();
      }).observe(document.body || document.documentElement, {
        childList: true,
        subtree:   true,
        characterData: true,
      });
    }

  }());


  /* =========================================================================
   * FIX 10 — HIDE STICKY ADD TO CART WHEN CART DRAWER IS OPEN
   *
   * When the slide-out cart drawer opens, the sticky "Add to Cart" bar at
   * the bottom of product pages overlaps the drawer, confusing the customer.
   * We detect the cart drawer open state by watching for common Shopify /
   * Reformation theme patterns and toggling a body class (gk-cart-open)
   * which the CSS uses to hide .gk-sticky-cta.
   * ========================================================================= */

  (function () {
    function setCartOpen(isOpen) {
      document.body.classList.toggle('gk-cart-open', isOpen);
    }

    function initCartDrawerWatcher() {
      /* --- Pattern 1: <details> or <div> with a cart-related ID/class --- */
      var drawerSelectors = [
        '#CartDrawer',
        '#cart-drawer',
        '.cart-drawer',
        '[id*="cart-drawer"]',
        '[data-drawer="cart"]',
      ];

      drawerSelectors.forEach(function (sel) {
        var drawer = document.querySelector(sel);
        if (!drawer || drawer._gkCartWatched) return;
        drawer._gkCartWatched = true;

        /* If it's a <details> element, listen to the toggle event */
        if (drawer.tagName === 'DETAILS') {
          drawer.addEventListener('toggle', function () {
            setCartOpen(drawer.open);
          });
        }

        /* MutationObserver for class/attribute changes (open, active, etc.) */
        new MutationObserver(function () {
          var isOpen = drawer.classList.contains('active') ||
                       drawer.classList.contains('open')   ||
                       drawer.classList.contains('is-open') ||
                       drawer.hasAttribute('open')         ||
                       drawer.getAttribute('aria-hidden') === 'false';
          setCartOpen(isOpen);
        }).observe(drawer, { attributes: true, attributeFilter: ['class', 'open', 'aria-hidden'] });
      });

      /* --- Pattern 2: body gets a drawer/cart class --- */
      new MutationObserver(function () {
        var bodyOpen = document.body.classList.contains('drawer--open') ||
                       document.body.classList.contains('cart-open')   ||
                       document.body.classList.contains('js-drawer-open-cart');
        /* Only set via this pattern if no drawer element found above */
        if (bodyOpen !== undefined) setCartOpen(bodyOpen);
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

      /* --- Pattern 3: Shopify Ajax Cart / theme events --- */
      document.addEventListener('cart:open',  function () { setCartOpen(true);  });
      document.addEventListener('cart:close', function () { setCartOpen(false); });
      document.addEventListener('drawer:open',  function (e) {
        if (e.detail && (e.detail.drawerType === 'cart' || (e.detail.id && e.detail.id.indexOf('cart') !== -1))) {
          setCartOpen(true);
        }
      });
      document.addEventListener('drawer:close', function () { setCartOpen(false); });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initCartDrawerWatcher);
    } else {
      initCartDrawerWatcher();
    }
    /* Re-run after a short delay to catch drawers injected by apps */
    setTimeout(initCartDrawerWatcher, 1500);

  }());




  /* =========================================================================
   * FIX 11 — PRE-ORDER: BUTTON TEXT + PRODUCT PAGE NOTICE
   *
   * ShopifyAnalytics.meta.product does NOT include tags — we fetch them from
   * /products/{handle}.js which always returns the full tag array.
   *
   * Once the 'preorder' tag is confirmed:
   *   1. span.single-add-to-cart-button--text → "Pre-order — Ships April 22"
   *   2. .gk-sticky-cta__btn text node        → "Pre-order — Ships April 22"
   *   3. Pre-order notice banner injected below the trust bar
   * ========================================================================= */

  (function () {
    var PREORDER_TAG      = 'preorder';
    var PREORDER_BTN_TEXT = 'Pre-order \u2014 Ships April\u00a022';
    var NOTICE_ID         = 'gk-preorder-notice';
    var _isPreorder       = null; /* cached result */

    /* --- get product handle from ShopifyAnalytics (always present) --- */
    function getHandle() {
      try {
        var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
        return meta && meta.product && meta.product.handle;
      } catch (e) { return null; }
    }

    /* --- fetch product JSON and check for preorder tag, then run callback --- */
    function withPreorderCheck(cb) {
      if (_isPreorder === true)  { cb(); return; }
      if (_isPreorder === false) { return; }

      var handle = getHandle();
      if (!handle) return;

      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) {
          _isPreorder = Array.isArray(p.tags) && p.tags.indexOf(PREORDER_TAG) !== -1;
          if (_isPreorder) cb();
        })
        .catch(function () {});
    }

    /* --- replace button text, preserving child elements (spinner SVG etc.) --- */
    function updatePreorderButtons() {
      /* Main ATC button span */
      document.querySelectorAll('.single-add-to-cart-button--text').forEach(function (el) {
        if (/^add to (cart|bag)$/i.test(el.textContent.trim())) {
          el.textContent = PREORDER_BTN_TEXT;
        }
      });

      /* Sticky CTA — text sits directly in the button, no wrapper span */
      document.querySelectorAll('.gk-sticky-cta__btn:not(.gk-sticky-cta__btn--sold-out)').forEach(function (btn) {
        btn.childNodes.forEach(function (node) {
          if (node.nodeType === Node.TEXT_NODE && /^add to (cart|bag)$/i.test(node.textContent.trim())) {
            node.textContent = PREORDER_BTN_TEXT;
          }
        });
        /* Fallback: no child nodes, full text replacement */
        if (btn.children.length === 0 && /^add to (cart|bag)$/i.test(btn.textContent.trim())) {
          btn.textContent = PREORDER_BTN_TEXT;
        }
      });
    }

    /* --- inject the pre-order notice banner below the trust bar --- */
    function injectPreorderNotice() {
      if (document.getElementById(NOTICE_ID)) return;

      var anchor = document.querySelector('.gk-trust-bar--product') ||
                   document.querySelector('.product-price-container') ||
                   document.querySelector('.product-form');
      if (!anchor) return;

      var notice = document.createElement('div');
      notice.id        = NOTICE_ID;
      notice.className = 'gk-preorder-notice';
      notice.innerHTML =
        '<span class="gk-preorder-notice__icon">📦</span>' +
        '<span class="gk-preorder-notice__text">' +
          '<strong>Pre-order \u2014 Ships April 22.</strong> ' +
          'This fragrance previously sold out. Secure yours early at <strong>$10 off</strong> ' +
          'the regular price. Your card is charged now; your order ships on April\u00a022.' +
        '</span>';

      anchor.parentElement.insertBefore(notice, anchor.nextSibling);
    }

    function applyAll() {
      withPreorderCheck(function () {
        updatePreorderButtons();
        injectPreorderNotice();
      });
    }

    /* -----------------------------------------------------------------------
     * COLLECTION / HOME PAGE — update each product card's ATC button
     * Card buttons carry data-product-handle so we can look up tags without
     * knowing in advance which products are preorders.
     * --------------------------------------------------------------------- */
    var _checkedHandles = {}; /* cache: handle → true/false */

    function updateCollectionCards() {
      document.querySelectorAll('.product-card--add-to-cart-button[data-product-handle]').forEach(function (btn) {
        var handle = btn.getAttribute('data-product-handle');
        if (!handle) return;

        /* Already checked and it's a preorder — update now */
        if (_checkedHandles[handle] === true) {
          applyCardPreorder(btn);
          return;
        }

        /* Already checked, not a preorder — skip */
        if (_checkedHandles[handle] === false) return;

        /* Not yet checked — fetch product JSON */
        _checkedHandles[handle] = 'pending';
        fetch('/products/' + handle + '.js')
          .then(function (r) { return r.json(); })
          .then(function (p) {
            var isPreorder = Array.isArray(p.tags) && p.tags.indexOf(PREORDER_TAG) !== -1;
            _checkedHandles[handle] = isPreorder;
            if (isPreorder) {
              /* Update all cards for this handle (may appear more than once) */
              document.querySelectorAll(
                '.product-card--add-to-cart-button[data-product-handle="' + handle + '"]'
              ).forEach(applyCardPreorder);
            }
          })
          .catch(function () { _checkedHandles[handle] = false; });
      });
    }

    function applyCardPreorder(btn) {
      var textEl = btn.querySelector('.product-card--add-to-cart-text');
      if (textEl && /^(add to (cart|bag)|pre-order)$/i.test(textEl.textContent.trim())) {
        textEl.textContent = 'Pre-order \u2014 Ships April\u00a022';
      }
      /* Gold button styling — add class if not already present */
      btn.classList.add('gk-preorder-btn');
    }

    function initAll() {
      applyAll();           /* product page */
      updateCollectionCards(); /* collection / home page cards */
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll);
    } else {
      initAll();
    }
    setTimeout(initAll, 800);
    setTimeout(initAll, 2500);

    /* Watch for theme re-rendering buttons after variant switches or lazy-load */
    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(function () {
        if (_isPreorder) updatePreorderButtons();
        updateCollectionCards();
      }).observe(document.body || document.documentElement, {
        childList: true, subtree: true,
      });
    }

  }());


  /* =========================================================================
   * FIX 12 — PRE-ORDER CART NOTICE
   *
   * When the cart contains at least one pre-order product (detected by fetching
   * each unique product handle via /products/{handle}.js and checking its tags),
   * a notice banner is shown at the top of the cart collaterals section so the
   * customer clearly understands they are paying for pre-order items.
   * ========================================================================= */

  (function () {
    var CART_NOTICE_ID = 'gk-preorder-cart-notice';
    var PREORDER_TAG   = 'preorder';

    function showCartPreorderNotice() {
      if (document.getElementById(CART_NOTICE_ID)) return;

      /* Find the best insertion point in the cart */
      var anchor = document.querySelector('.thb-cart-form--cart-collaterals') ||
                   document.querySelector('form[action="/cart"]') ||
                   document.querySelector('.cart__footer');
      if (!anchor) return;

      var notice = document.createElement('div');
      notice.id        = CART_NOTICE_ID;
      notice.className = 'gk-preorder-cart-notice';
      notice.innerHTML =
        '<span class="gk-preorder-cart-notice__icon">📦</span>' +
        '<span class="gk-preorder-cart-notice__text">' +
          '<strong>Your cart contains pre-order items.</strong> ' +
          'These will ship on <strong>April 22</strong>. You\'ll receive a separate ' +
          'pre-order confirmation email with full details.' +
        '</span>';

      anchor.insertBefore(notice, anchor.firstChild);
    }

    function removeCartPreorderNotice() {
      var el = document.getElementById(CART_NOTICE_ID);
      if (el) el.parentElement.removeChild(el);
    }

    /* Fetch each unique product handle from the cart and check for preorder tag */
    function checkCartForPreorders() {
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart.items || cart.items.length === 0) {
            removeCartPreorderNotice();
            return;
          }

          /* Collect unique handles */
          var handles = [];
          cart.items.forEach(function (item) {
            if (item.handle && handles.indexOf(item.handle) === -1) {
              handles.push(item.handle);
            }
          });

          /* Fetch each product and check for preorder tag */
          var checks = handles.map(function (handle) {
            return fetch('/products/' + handle + '.js')
              .then(function (r) { return r.json(); })
              .then(function (p) {
                return Array.isArray(p.tags) && p.tags.indexOf(PREORDER_TAG) !== -1;
              })
              .catch(function () { return false; });
          });

          Promise.all(checks).then(function (results) {
            var hasPreorder = results.some(function (v) { return v; });
            if (hasPreorder) {
              showCartPreorderNotice();
            } else {
              removeCartPreorderNotice();
            }
          });
        })
        .catch(function () {});
    }

    /* Only run on cart page */
    if (window.location.pathname.indexOf('/cart') !== -1 ||
        document.querySelector('form[action="/cart"]')) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', checkCartForPreorders);
      } else {
        checkCartForPreorders();
      }
      setTimeout(checkCartForPreorders, 1000);
    }

  }());


}(window, document));